import {
	AddonProtected,
	AuthenticatedSessions,
	H1,
	LinkTab,
	LinkTabs,
	Loading,
	NotFound,
	Section,
	UserService,
	useIsAddonEnabled,
} from "@dgs/core";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Route, Routes, useSearchParams } from "react-router";
import styled from "styled-components";
import { Addon } from "../../../shared/types/addon";
import { useFrontendConfig } from "../config/useFrontendConfig";
import { usePreferencesQuery } from "../shared/api";
import { BackAnchor } from "../shared/ui/Back";
import { BaseProfileEdit } from "./BaseProfileEdit";
import { ConnectedAccounts } from "./ConnectedAccounts";
import { MyPreferences } from "./preferences/MyPreferences";

const Container = styled(Section).attrs({ color: "content" })`
	padding: ${({ theme }) => theme.spacing(6)} ${({ theme }) => theme.spacing(2)};
`;

const Heading = styled(H1)`
	margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const Header = styled.div`
	display: flex;
	flex-direction: column;
`;

const StyledLinkTabs = styled(LinkTabs)`
	margin-bottom: ${({ theme }) => theme.spacing(6)};
`;

export const ProfilePage: FC = () => {
	const { t } = useTranslation();
	const { userUrl } = useFrontendConfig();
	const userService = useMemo(() => new UserService(userUrl), [userUrl]);
	const { data: preferences, isLoading } = usePreferencesQuery();
	const [searchParams] = useSearchParams();
	const redirectTo = searchParams.get("redirect");
	const arePreferencesEnabled = useIsAddonEnabled(Addon.PREFERENCES);

	return (
		<Container>
			<Header>
				{redirectTo && <BackAnchor href={redirectTo} />}
				<Heading>{t("My account")}</Heading>
			</Header>
			{isLoading && <Loading />}
			<StyledLinkTabs>
				<LinkTab to={redirectTo ? `../details?redirect=${redirectTo}` : "../details"}>{t("Profile")}</LinkTab>
				{arePreferencesEnabled && preferences.length > 0 && (
					<LinkTab to={redirectTo ? `../preferences?redirect=${redirectTo}` : "../preferences"}>
						{t("Preferences")}
					</LinkTab>
				)}
				<LinkTab to={redirectTo ? `../connected-accounts?redirect=${redirectTo}` : "../connected-accounts"}>
					{t("Connected accounts")}
				</LinkTab>
				<LinkTab to={redirectTo ? `../authenticated-sessions?redirect=${redirectTo}` : "../authenticated-sessions"}>
					{t("Authenticated sessions")}
				</LinkTab>
			</StyledLinkTabs>

			<Routes>
				<Route index element={<Navigate to="details" replace={true} />} />
				<Route path="details" element={<BaseProfileEdit userService={userService} />} />
				<Route
					path="preferences"
					element={
						<AddonProtected addon={Addon.PREFERENCES}>
							<MyPreferences preferences={preferences} />
						</AddonProtected>
					}
				/>
				<Route path="connected-accounts" element={<ConnectedAccounts />} />
				<Route path="authenticated-sessions" element={<AuthenticatedSessions service={userService} />} />
				<Route path="*" element={<NotFound />} />
			</Routes>
		</Container>
	);
};
